<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { formatDate } from '@/utils/dateFormatter'
import { IExtendedCertificate } from '@shared/types/extensions'
import { ref as dbRef, get, query, orderByChild, equalTo, onValue } from 'firebase/database'
import { database } from '@/firebase/config'
import { getAuth } from 'firebase/auth'
import { certificateService } from '@/services/certificateService'
import { ICertificatePackage, ICertificate } from '@shared/types'
import ViewDetailsModal from '@/components/certificados/ViewDetailsModal.vue'
import RequestSignModal from '@/components/certificados/RequestSignModal.vue'
import { useStore } from 'vuex'
import BaseModal from '@/components/BaseModal.vue'
import { Assets } from '@/types/assets'
import '@/styles/virtualized-components.css'

const assets = Assets.getInstance()

const auth = getAuth()
const db = database

const props = defineProps<{
	packages: ICertificatePackage[]
	institutionId: string
	testMode: boolean
	currentInstitution: string
}>()

const emit = defineEmits(['package-removed', 'refresh-packages'])
// Computed properties para mejor reactividad
const institutionPath = computed(() => (path: string) => `institutions/${props.institutionId}${props.testMode ? '/testMode' : ''}${path}`)

const certificatesPath = computed(() => institutionPath.value('/certificates/queue'))

const columns = [
	{ key: 'id', label: 'ID' },
	{ key: 'label', label: 'Nombre' },
	{ key: 'fecha', label: 'Fecha' },
	{ key: 'sign', label: 'Estado' },
	{ key: 'actions', label: 'Acciones' }
]

const store = useStore()
// Reactive refs
const selectedPackage = ref<ICertificatePackage | null>(null)
const loading = ref(false)
const cadenasAFirmar = ref<string[]>([])
const deviceId = ref<string>('')
const certificatesToSign = ref<ICertificate[]>([])
const certificatesListPackage = ref<ICertificate[]>([])
const showModalViewDetails = ref(false)
const showModalRequestSign = ref(false)
const showErrorSignModal = ref(false)
const errorSignMessage = ref('')
const responsablesCertificate = ref<string[]>([])
const userCurp = computed(() => store.state.global.user.me.curp || '')
const errorMessage = ref<string>('')
const showErrorModal = ref(false)
// Referencia para el listener de Firebase
let certificatesListener: (() => void) | null = null

// Cleanup function para listeners
const cleanupListeners = () => {
	if (certificatesListener) {
		certificatesListener()
		certificatesListener = null
	}
}

// Cleanup al desmontar el componente
onUnmounted(() => {
	cleanupListeners()
})

// View package details
const viewPackage = async (pkg: ICertificatePackage) => {
	selectedPackage.value = pkg
	await loadCertificatesPackage().then(() => {
		showModalViewDetails.value = true
	})
}

const loadCertificatesPackage = async (reset = false) => {
	if (reset) {
		certificatesListPackage.value = []
	}

	try {
		// Check if user is authenticated
		if (!auth.currentUser) {
			throw new Error('User not authenticated')
		}

		// Check if institution ID is available
		if (!props.institutionId) {
			throw new Error('Institution ID not available')
		}

		if (!selectedPackage.value) {
			throw new Error('No hay paquete seleccionado')
		}

		// Limpiar listener anterior
		cleanupListeners()

		// Obtener los certificados del paquete actual
		const certificatesRef = dbRef(db, certificatesPath.value)

		certificatesListener = onValue(certificatesRef, snapshot => {
			if (snapshot.exists()) {
				const allCertificates = snapshot.val()
				const packageCertificates = Object.entries(selectedPackage.value?.certificates || {})
					.map(([certificateId]) => {
						if (allCertificates[certificateId]) {
							return {
								id: certificateId,
								...allCertificates[certificateId],
								selected: false
							}
						}
						return null
					})
					.filter(cert => cert !== null)
				certificatesListPackage.value = packageCertificates
			} else {
				certificatesListPackage.value = []
			}
		})
	} catch (error) {
		console.error('Error loading certificates:', error)
		if (error instanceof Error) {
			if (error.message === 'User not authenticated') {
				errorMessage.value = 'Por favor inicie sesión para continuar'
				showErrorModal.value = true
			} else if (error.message === 'Institution ID not available') {
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
				showErrorModal.value = true
			} else if (error.message === 'No hay paquete seleccionado') {
				errorMessage.value = 'No hay paquete seleccionado para mostrar los certificados.'
				showErrorModal.value = true
			} else if (error.message.includes('Permission denied')) {
				errorMessage.value = 'No tiene permisos para acceder a los certificados. Por favor contacte al administrador.'
				showErrorModal.value = true
			} else {
				errorMessage.value = 'Error al cargar los certificados: ' + error.message
				showErrorModal.value = true
			}
		} else {
			errorMessage.value = 'Error al cargar los certificados'
			showErrorModal.value = true
		}
	}
}

// Remove package
const removePackage = async (index: number) => {
	const package_ = props.packages[index]
	if (!package_) return

	if (!confirm('¿Está seguro de eliminar este paquete? Los certificados volverán a estar disponibles para crear un nuevo paquete.')) {
		return
	}
	try {
		loading.value = true
		await certificateService.deletePackage(package_.id, props.institutionId, props.testMode)
		emit('package-removed')
	} catch (error) {
		console.error('Error al eliminar el paquete:', error)
		let errorMessageValue = 'Ocurrió un error al eliminar el paquete, intentelo nuevamente. Si el problema persiste, solicite ayuda en ' + assets.vendorEmail
		if (error instanceof Error) {
			errorMessageValue = error.message
		}
		showErrorModal.value = true
		errorMessage.value = errorMessageValue
	} finally {
		loading.value = false
	}
}

const requestSignPackage = async (packageId: string) => {
	const packageToSign = props.packages.find(p => p.id.toString() === packageId)
	selectedPackage.value = packageToSign as ICertificatePackage
	try {
		loading.value = true
		const paqueteFirmado = packageToSign?.sign || false

		// Si el paquete ya está firmado, no necesitamos cargar las cadenas
		if (paqueteFirmado) {
			showModalRequestSign.value = true
			return
		}

		// Obtener las cadenas originales del paquete
		const certificatesRef = dbRef(db, certificatesPath.value)
		const packageQuery = query(certificatesRef, orderByChild('package'), equalTo(packageToSign?.id || ''))
		const snapshot = await get(packageQuery)

		if (!snapshot.exists()) {
			errorSignMessage.value = 'No se encontraron certificados en el paquete'
			showErrorSignModal.value = true
			return
		}

		const documents = snapshot.val()
		cadenasAFirmar.value = []

		// Recolectar todas las cadenas originales
		for (const [, certificateData] of Object.entries(documents)) {
			const certificate = certificateData as IExtendedCertificate & {
				cadenaOriginal: string | Record<string, string>
			}
			Object.keys(certificate.data?.FirmaResponsables || {}).forEach(curp => {
				if (!responsablesCertificate.value.includes(curp)) {
					responsablesCertificate.value.push(curp)
				}
			})
			// Verificar que el usuario esté asignado como responsable en este certificado
			if (!certificate.data?.FirmaResponsables?.[userCurp.value]) {
				errorSignMessage.value = `El usuario con CURP ${userCurp.value} no está asignado como responsable en el certificado ${certificate.folioControl}`
				showErrorSignModal.value = true
				continue
			}
			certificatesToSign.value.push(certificate)

			if (typeof certificate.cadenaOriginal === 'object') {
				// Si cadenaOriginal es un objeto, agregar todas las cadenas
				Object.values(certificate.cadenaOriginal).forEach(cadena => {
					if (cadena) {
						cadenasAFirmar.value.push(cadena as string)
					}
				})
			} else if (certificate.cadenaOriginal) {
				// Si cadenaOriginal es un string, agregarlo directamente
				cadenasAFirmar.value.push(certificate.cadenaOriginal)
			}
		}
		if (cadenasAFirmar.value.length === 0) {
			errorSignMessage.value = `No se encontraron cadenas originales para firmar con tu CURP (${userCurp.value}).`
			showErrorSignModal.value = true
			return
		}
		// Obtener el deviceId usando el ID del usuario conectado
		if (auth.currentUser?.uid) {
			deviceId.value = `WEB.${auth.currentUser.uid}`
		} else {
			throw new Error('Usuario no autenticado')
		}

		showModalRequestSign.value = true
	} catch (error: any) {
		console.error('Error al preparar el paquete para firma:', error)
		showErrorModal.value = true
		errorMessage.value = `Error: ${error.message}`
	} finally {
		loading.value = false
		emit('refresh-packages')
	}
}

const closeErrorSignModal = () => {
	showErrorSignModal.value = false
	responsablesCertificate.value = []
	errorSignMessage.value = ''
}
</script>
<template>
	<div class="package-view-container certificados-scroll-list">
		<VaDataTable :items="packages" :columns="columns" class="package-table">
			<template #cell(id)="{ rowData }">
				<div class="package-id-cell">
					<VaIcon name="inventory_2" color="primary" class="package-icon" />
					<span class="package-id-text">{{ rowData.id }}</span>
				</div>
			</template>

			<template #cell(label)="{ rowData }">
				<div class="package-label-cell">
					<VaIcon name="description" color="primary" class="label-icon" />
					<span class="package-label-text">{{ rowData.label }}</span>
				</div>
			</template>

			<template #cell(fecha)="{ rowData }">
				<div class="package-date-cell">
					<VaIcon name="event" color="primary" class="date-icon" />
					<span class="package-date-text">{{ formatDate(rowData.id) }}</span>
				</div>
			</template>

			<template #cell(sign)="{ rowData }">
				<div class="package-status-cell">
					<VaBadge v-if="rowData.sign" color="transparent" text="Firmado" class="status-badge status-signed">
						<template #prepend>
							<VaIcon name="check_circle" class="status-icon" />
						</template>
					</VaBadge>
					<VaBadge v-else color="transparent" text="Pendiente" class="status-badge status-pending">
						<template #prepend>
							<VaIcon name="pending" class="status-icon" />
						</template>
					</VaBadge>
				</div>
			</template>

			<template #cell(actions)="{ rowData, rowIndex }">
				<div class="package-actions-cell">
					<div class="actions-container">
						<!-- Ver detalles -->
						<VaPopover message="Ver detalles del paquete" class="action-popover">
							<VaButton preset="plain" icon="visibility" class="action-button" @click="viewPackage(rowData)" />
						</VaPopover>

						<!-- Solicitar firma -->
						<VaPopover message="Solicitar firma del paquete" class="action-popover">
							<VaButton preset="plain" icon="edit_square" class="action-button btn-primary" @click="requestSignPackage(rowData.id.toString())" />
						</VaPopover>

						<!-- Eliminar paquete -->
						<VaPopover message="Eliminar paquete" class="action-popover">
							<VaButton preset="plain" icon="delete" color="danger" class="action-button" @click="removePackage(rowIndex)" :disabled="rowData.sign" />
						</VaPopover>
					</div>
				</div>
			</template>
		</VaDataTable>
	</div>
	<ViewDetailsModal v-model="showModalViewDetails" :package="selectedPackage" :isTestMode="testMode" :certificatesList="certificatesListPackage" :currentInstitution="props.currentInstitution" />

	<RequestSignModal
		v-model="showModalRequestSign"
		:package="selectedPackage"
		:selectedInstitution="store.state.global.selectedInstitution"
		:currentInstitution="currentInstitution"
		:isTestMode="testMode"
		:cadenasAFirmar="cadenasAFirmar"
		:deviceId="deviceId" />
	<!-- Modal de Firma -->
	<BaseModal v-model="showErrorSignModal" title="Advertencia" :test-mode="testMode" :message="errorSignMessage" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Advertencia en la Firma</h3>
				</div>

				<div class="error-content">
					<p class="error-description">{{ errorSignMessage }}</p>

					<div class="responsables-info" v-if="responsablesCertificate.length > 0">
						<div class="responsables-header">
							<i class="fas fa-users"></i>
							<h4>Responsables asignados en los certificados:</h4>
						</div>
						<div class="responsables-list">
							<div v-for="degree in responsablesCertificate" :key="degree" class="responsable-item">
								<i class="fas fa-user-graduate"></i>
								<span>{{ degree }}</span>
								<VaBadge color="transparent" text="Pendiente de firma" class="status-badge status-pending"></VaBadge>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="closeErrorSignModal">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
/* Contenedor Principal: scroll delegado al padre; espacio final unificado en virtualized-components.css */
.package-view-container {
	height: 100%;
	overflow: visible;
	padding: 1rem;
}

/* Tabla Mejorada */
.package-table {
	height: auto;
	min-height: 100%;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	animation: fadeIn 0.5s ease-in-out;
}

/* Estilos de la Tabla VaDataTable */
.package-table :deep(.va-data-table) {
	height: auto;
	border-radius: 12px;
	overflow: hidden;
}

.package-table :deep(.va-data-table__table-wrapper) {
	height: auto;
	overflow: visible;
	border-radius: 12px;
}

.package-table :deep(.va-data-table__table) {
	width: 100%;
	border-collapse: collapse;
	font-family: Arial, sans-serif;
	background-color: #f9f9f9;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	border: 2px solid #1a4d42;
	border-radius: 8px;
}

.package-table :deep(.va-data-table__table-container) {
	height: auto;
	overflow: visible;
	border-radius: 12px;
}

/* Header de la Tabla */
.package-table :deep(.va-data-table__thead) {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.package-table :deep(.va-data-table__th) {
	padding: 1rem;
	color: white;
	font-weight: 600;
	text-align: left;
	border: 1px solid #dddddd;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

/* Filas de la Tabla */
.package-table :deep(.va-data-table__tbody tr) {
	border-bottom: 1px solid #dddddd;
	transition: background-color 0.2s ease;
}

.package-table :deep(.va-data-table__tbody tr:hover) {
	background-color: #f0f8ff;
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.package-table :deep(.va-data-table__tbody tr:nth-of-type(even)) {
	background-color: #f3f3f3;
}

.package-table :deep(.va-data-table__tbody tr:nth-of-type(even):hover) {
	background-color: #e6f3ff;
}

.package-table :deep(.va-data-table__tbody tr:last-of-type) {
	border-bottom: 2px solid #1a4d42;
}

/* Celdas de la Tabla */
.package-table :deep(.va-data-table__td) {
	padding: 1rem;
	border: 1px solid #dddddd;
	vertical-align: middle;
}

/* Celdas Específicas */
.package-id-cell,
.package-label-cell,
.package-date-cell,
.package-status-cell,
.package-actions-cell {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.5rem;
	border-radius: 8px;
	transition: all 0.3s ease;
}

.package-id-cell:hover,
.package-label-cell:hover,
.package-date-cell:hover {
	background: rgba(0, 123, 255, 0.05);
}

/* Iconos de Celdas */
.package-icon,
.label-icon,
.date-icon {
	font-size: 1.125rem;
	flex-shrink: 0;
}

/* Textos de Celdas */
.package-id-text,
.package-label-text,
.package-date-text {
	font-weight: 500;
	color: #1a2a3a;
}

/* Celda de Estado */
.package-status-cell {
	justify-content: center;
}

/* Badges de Estado Mejorados */
.status-badge {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 12px;
	font-size: 0.875rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	transition: all 0.3s ease;
}

.status-signed {
	background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
	color: #166534;
	border: 1px solid #86efac;
}

.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.status-icon {
	font-size: 1rem;
}

/* Celda de Acciones */
.package-actions-cell {
	justify-content: center;
}

.actions-container {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	justify-content: center;
}

.action-popover {
	margin: 0 0.25rem;
}

/* Botones de Acción */
.action-button {
	transition: all 0.3s ease;
	border-radius: 8px;
	padding: 0.5rem;
	min-width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.action-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Botón Primario */
.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design */
@media (max-width: 768px) {
	.package-view-container {
		padding: 0.5rem;
	}

	.actions-container {
		flex-direction: column;
		gap: 0.25rem;
	}

	.action-button {
		min-width: 36px;
		height: 36px;
	}

	.package-table :deep(.va-data-table__th),
	.package-table :deep(.va-data-table__td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.875rem;
	}

	.package-id-cell,
	.package-label-cell,
	.package-date-cell,
	.package-status-cell,
	.package-actions-cell {
		padding: 0.25rem;
		gap: 0.5rem;
	}
}

/* Estados de Carga */
.package-table :deep(.va-data-table--loading) {
	opacity: 0.7;
	pointer-events: none;
}

/* Scrollbar Personalizado */
.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar {
	width: 8px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Estilos para el Mensaje de Error */

.error-content {
	color: #000000;
}

.responsables-info {
	background: rgba(255, 255, 255, 0.8);
	border-radius: 12px;
	padding: 1.5rem;
	border: 1px solid #fef0ca;
}

.responsables-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
}

.responsables-header i {
	color: #d8c426;
	font-size: 1.25rem;
}

.responsables-header h4 {
	margin: 0;
	color: #000000;
	font-size: 1.1rem;
	font-weight: 600;
}

.responsables-list {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.responsable-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem 1rem;
	background: linear-gradient(135deg, #fff 0%, #f7fafc 100%);
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	transition: all 0.3s ease;
}

.responsable-item:hover {
	transform: translateX(4px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	border-color: #d8c426;
}

.responsable-item i {
	color: #d8c426;
	font-size: 1rem;
}

.responsable-item span {
	font-weight: 500;
	color: #2d3748;
}

/* Animaciones para el Error */
@keyframes slideInError {
	from {
		opacity: 0;
		transform: translateY(-20px) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}
</style>
